import { selectorFamily } from 'recoil';
import type { BlockId } from '../studio/types';
import { blockInfoGraphicType } from '../studio/configuration/blockConfig';
import { blockSettings } from '../studio/configuration/blockSettings';
import { studioPrintOrientationType, studioFooterHeight } from '../studio/configuration/print';
import {
  blockSourcePageVisibleHeight,
  blockTitleBarHeightState,
  blockColumnHeaderHeightState,
  blockDataRowHeightState,
  continuationDataMaxPx,
} from './blockExpansion';
import type { BlockTypeArgs } from './expandableBlockTypes';
import { isCustomSliceExpandableBlockType, isVirtualizedSliceBlockType } from './expandableBlockTypes';

const blockTypeArgs = selectorFamily<BlockTypeArgs, BlockId>({
  key: 'blockTypeArgs',
  get:
    (blockId) =>
    ({ get }) => ({
      customBlockType: get(blockSettings(blockId))?.customBlockType,
      infoGraphicType: get(blockInfoGraphicType(blockId)),
    }),
});

export const blockCustomExpandsByRowSlice = selectorFamily<boolean, BlockId>({
  key: 'blockCustomExpandsByRowSlice',
  get:
    (blockId) =>
    ({ get }) =>
      isCustomSliceExpandableBlockType(get(blockTypeArgs(blockId))),
});

export const blockIsVirtualizedSlice = selectorFamily<boolean, BlockId>({
  key: 'blockIsVirtualizedSlice',
  get:
    (blockId) =>
    ({ get }) =>
      isVirtualizedSliceBlockType(get(blockTypeArgs(blockId))),
});

export const blockCustomSliceRange = selectorFamily<{ sourceRowCount: number; continuationRowCount: number }, BlockId>({
  key: 'blockCustomSliceRange',
  get:
    (blockId) =>
    ({ get }) => {
      const sourceVisibleHeight = get(blockSourcePageVisibleHeight(blockId));
      const titleBarPx = get(blockTitleBarHeightState(blockId));
      const columnHeaderPx = get(blockColumnHeaderHeightState(blockId));
      const dataRowHeight = get(blockDataRowHeightState(blockId));
      const orientation = get(studioPrintOrientationType);
      const footerHeightPx = get(studioFooterHeight);

      const dataAreaInSource = Math.max(0, sourceVisibleHeight - titleBarPx - columnHeaderPx);
      const sourceRowCount = dataRowHeight > 0 ? Math.floor(dataAreaInSource / dataRowHeight) : 0;

      const dataPerSlicePx = continuationDataMaxPx({
        orientation,
        columnHeaderHeightPx: columnHeaderPx,
        footerHeightPx,
        dataRowHeightPx: dataRowHeight,
      });
      const continuationRowCount = dataRowHeight > 0 ? Math.floor(dataPerSlicePx / dataRowHeight) : 0;

      return { sourceRowCount, continuationRowCount };
    },
});
